import { FC, useEffect, useState } from 'react';
import { Button, Typography, Form } from 'antd';
import classNames from 'classnames';

import { getUserSettings, updateUserSettings } from 'features/userSettings/actions';
import { getLoyalty } from 'features/overview/actions';

import { Skeleton } from 'componets/ui';
import { AccountLayout } from 'componets/layouts';
import { SettingsCard } from './SettingsCard';
import { SettingsDisabledInput } from './SettingsDisabledInput';
import { SettingNotificationCard } from './SettingNotificationCard';
import { SettingLoyaltyCard } from './SettingLoyaltyCard';
import { SettingsAddressCard } from './SettingsAddressCard';
import { SettingsLanguageCard } from './SettingsLanguageCard';
import { SettingsResidencyCard } from './SettingsResidencyCard';
import { SettingsReferralCard } from './SettingsReferralCard';
import { SettingsPersonalBonusCard } from './SettingsPersonalBonusCard';
import { SettingsSecurityCard } from './SettingsSecurityCard';
import { SettingsPersonalInformationCard } from './SettingsPersonalInformationCard';
import { SettingsBankAccountCards } from './SettingsBankAccountCards';
import { SettingsDeleteAccount } from './SettingsDeleteAccount';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { FieldData } from 'models';

import { ClientRole } from 'utils/constants';
import Utils from 'utils/utils';
import { setCurrentLanguage } from 'utils';
import { language } from 'utils/i18n';

import { selectIsAdmin, selectUser } from 'selectors';

import s from './index.module.scss';

const WORLDS = [
	'companyRegistrationNumberText',
	'saveChangesText',
	'companyNameText',
	'companyInformationText',
	'rewardProgramsText',
];

export const Settings: FC = () => {
	return (
		<AccountLayout>
			<SettingsForm />
		</AccountLayout>
	);
};

const SettingsForm = () => {
	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const settings = useAppSelector((state) => state.userSettings.entity);
	const user = useAppSelector(selectUser);
	const isAdmin = useAppSelector(selectIsAdmin);

	const {
		companyRegistrationNumberText,
		saveChangesText,
		companyNameText,
		companyInformationText,
		rewardProgramsText,
	} = useTranslate(WORLDS);

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(getUserSettings());
		dispatch(getLoyalty());
	}, [dispatch]);

	const onFinish = (data: any) => {
		setIsSubmitting(true);

		dispatch(updateUserSettings({ ...data }))
			.unwrap()
			.then(() => {
				if (data.language !== language) {
					setCurrentLanguage(data.language).then();

					const langPath = window.location.pathname.split('/')[1];

					if (langPath !== data.language) {
						window.location.href = window.location.pathname.replace(langPath, data.language);
					}
				}
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
				Utils.scrollToFirstError(errors, form);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const initialValues = {
		country_id: settings.country_id,
		city: settings.city,
		address: settings.address,
		zip: settings.zip,
		language,
		phone: settings.phone,
	};

	return (
		<Skeleton loading={!settings?.country_id}>
			<div className={s.root}>
				<Form
					encType="multipart/form-data"
					className={s.form}
					form={form}
					onFinish={onFinish}
					onFieldsChange={setFields}
					fields={fields}
					initialValues={initialValues}
					scrollToFirstError={{
						behavior: 'smooth',
						block: 'center',
					}}
				>
					<SettingsPersonalInformationCard />

					{user.userType === ClientRole.COMPANY && (
						<SettingsCard title={companyInformationText}>
							<div className={s.contentRow}>
								<SettingsDisabledInput
									className={s.col}
									name={companyNameText}
									value={settings.company_name}
								/>
								<SettingsDisabledInput
									className={s.col}
									name={companyRegistrationNumberText}
									value={settings.id_number}
								/>
							</div>
						</SettingsCard>
					)}

					<SettingsAddressCard />

					<SettingsLanguageCard />

					<div>
						<Button
							type="primary"
							htmlType="submit"
							className={classNames(s.submitBtn, { 'ant-btn-grey': isAdmin })}
							loading={isSubmitting}
						>
							{saveChangesText}
						</Button>
					</div>
				</Form>

				<div className={s.reward}>
					<SettingsResidencyCard />

					{user.accounts?.length > 0 && <SettingsBankAccountCards />}

					<SettingNotificationCard />

					<SettingsSecurityCard />
				</div>

				<div className={s.reward}>
					<Typography.Title level={4}>{rewardProgramsText} 🏆</Typography.Title>

					{(user.personalInterests.length > 0 ||
						(!user.category?.isDefault &&
							!user.category?.isLoyalty &&
							+user.category?.interestRate > 0)) && <SettingsPersonalBonusCard />}

					<SettingLoyaltyCard />

					<SettingsReferralCard />
				</div>

				<SettingsDeleteAccount />
			</div>
		</Skeleton>
	);
};
